import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, Info, Monitor, Lock, X, Printer, FileText, CheckSquare, Palette, Sparkles } from 'lucide-react';
import { useCutList } from '../contexts/CutListContext';
import { useAuth } from '../contexts/AuthContext';
import SheetRenderer from '../shared/SheetRenderer';
import { getPartColor } from '../utils/colors';
import SetupStep from './steps/SetupStep';
import PartsStep from './steps/PartsStep';
import LayoutStep from './steps/LayoutStep';
import ExecutionStep from './steps/ExecutionStep';

const STEPS = [
  { id: 'setup', label: 'Setup', component: SetupStep },
  { id: 'parts', label: 'Parts', component: PartsStep },
  { id: 'layout', label: 'Layout', component: LayoutStep },
  { id: 'cut', label: 'Cut', component: ExecutionStep },
];

const STORAGE_KEY = 'cutlist-mobile-step';

/**
 * Mobile App - "The Assistant"
 * Wizard-style linear flow designed for use in the garage or hardware store
 * Focus on clarity, large touch targets, and a final static reference
 */
const MobileApp = ({ onShowAbout, onSwitchToDesktop }) => {
  const { pieces, layouts, sheetWidth, sheetLength, stockSheetTypes, currentUnits } = useCutList();
  const { isPro, promptUpgrade } = useAuth();

  // Preview modal state for free users
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Restore step from localStorage
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved) : 0;
  });

  // Persist step to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, currentStep.toString());
  }, [currentStep]);

  const canGoNext = () => {
    switch (currentStep) {
      case 0: // Setup
        return true; // Always can proceed from setup
      case 1: // Parts
        return pieces.length > 0;
      case 2: // Layout
        return layouts.length > 0;
      case 3: // Execution
        return false; // Final step
      default:
        return false;
    }
  };

  const canGoBack = () => currentStep > 0;

  const goNext = () => {
    if (canGoNext() && currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const goBack = () => {
    if (canGoBack()) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const goToStep = (stepIndex) => {
    // Only allow going to completed steps or current step
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
      window.scrollTo(0, 0);
    }
  };

  const CurrentStepComponent = STEPS[currentStep].component;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Progress Stepper */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => goToStep(index)}
                disabled={index > currentStep}
                className={`flex flex-col items-center ${
                  index > currentStep ? 'opacity-40' : ''
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    index < currentStep
                      ? 'bg-sage-500 text-white'
                      : index === currentStep
                      ? 'bg-accent-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index < currentStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={`text-xs mt-1 ${
                    index === currentStep
                      ? 'text-accent-500 font-semibold'
                      : 'text-gray-500'
                  }`}
                >
                  {step.label}
                </span>
              </button>

              {/* Connector Line */}
              {index < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 ${
                    index < currentStep ? 'bg-sage-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto">
        <CurrentStepComponent onNext={goNext} onBack={goBack} />
      </div>

      {/* Navigation Footer */}
      {currentStep < STEPS.length - 1 && (
        <div className="bg-white border-t border-gray-200">
          {/* Free Version Banner - only shown when not Pro */}
          {!isPro && (
            <button
              onClick={() => promptUpgrade('Upgrade to Pro for advanced optimization algorithms and more features.')}
              className="w-full bg-[#eb5324] text-white text-sm font-medium py-2 px-4 hover:bg-[#d44a1f] transition-colors"
            >
              Free Version — Tap to upgrade to Pro
            </button>
          )}

          {/* Main Footer Controls */}
          <div className="px-4 py-3 flex items-center justify-between">
            <button
              onClick={goBack}
              disabled={!canGoBack()}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                canGoBack()
                  ? 'text-gray-700 hover:bg-gray-100'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>

            <div className="flex items-center gap-2">
              {/* Pro Badge - only shown when Pro */}
              {isPro && (
                <span className="flex items-center justify-center px-2 py-1 text-xs font-medium border border-sage-500 text-sage-600 rounded-lg">
                  Pro
                </span>
              )}
              {onShowAbout && (
                <button
                  onClick={onShowAbout}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="About CutCalc"
                >
                  <Info className="w-4 h-4" />
                </button>
              )}
              {onSwitchToDesktop && (
                <button
                  onClick={onSwitchToDesktop}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Switch to Desktop View"
                >
                  <Monitor className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Export button - shows preview for free users on Layout step */}
            {currentStep === 2 && !isPro ? (
              <button
                onClick={() => setShowPreviewModal(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold bg-accent-400 text-white hover:bg-accent-500 transition-colors"
              >
                Preview
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={goNext}
                disabled={!canGoNext()}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  canGoNext()
                    ? 'bg-accent-400 text-white hover:bg-accent-500'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {currentStep === 2 ? 'Export' : 'Next'}
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Shop Sheet Preview Modal for Free Users */}
      {showPreviewModal && layouts.length > 0 && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#F4F7F5' }}>
          {/* Preview Banner - Fixed at top */}
          <div className="bg-[#D44A1F] px-4 py-2.5 flex items-center justify-center relative">
            <p className="text-sm text-white font-medium text-center">
              Preview Mode — Upgrade to unlock full functionality
            </p>
            <button
              onClick={() => setShowPreviewModal(false)}
              className="absolute right-2 p-1 text-white/80 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Preview Content */}
          <div className="flex-1 overflow-auto p-4">
            <div className="filter blur-sm pointer-events-none opacity-75">
              {layouts.slice(0, 8).map((layout, index) => {
                const stockType = layout.stockTypeId
                  ? stockSheetTypes?.find(t => t.id === layout.stockTypeId)
                  : null;

                // Group pieces for parts table
                const layoutPieces = layout.cutSequence?.filter(cut => cut.type === 'piece') || layout.pieces || [];
                const groupedParts = [];
                const partMap = new Map();
                layoutPieces.forEach(item => {
                  const p = item.piece || item;
                  const key = `${p.name}|${p.width?.toFixed(4)}|${p.length?.toFixed(4)}`;
                  if (!partMap.has(key)) {
                    partMap.set(key, { num: groupedParts.length + 1, piece: p, qty: 0 });
                    groupedParts.push(partMap.get(key));
                  }
                  partMap.get(key).qty++;
                });
                const numToLetter = (n) => {
                  let s = '';
                  while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); }
                  return s;
                };

                return (
                  <div key={layout.id || index} className="bg-white rounded-lg p-3 mb-3 border border-gray-200">
                    <div className="text-center mb-2">
                      <p className="text-xs text-gray-500">
                        Sheet {index + 1} {stockType ? `• ${stockType.name}` : ''}
                      </p>
                    </div>
                    <div className="flex justify-center mb-3">
                      <SheetRenderer
                        layout={layout}
                        mode="interactive"
                        maxWidth={280}
                        showLabels={true}
                        showDimensions={false}
                        numberLabels={true}
                      />
                    </div>
                    {/* Parts Table */}
                    {groupedParts.length > 0 && (
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="px-1.5 py-1 text-[9px] font-bold uppercase text-gray-500 w-10">Part</th>
                              <th className="px-1.5 py-1 text-[9px] font-bold uppercase text-gray-500 w-10">Qty</th>
                              <th className="px-1.5 py-1 text-[9px] font-bold uppercase text-gray-500 w-14">W</th>
                              <th className="px-1.5 py-1 text-[9px] font-bold uppercase text-gray-500 w-14">L</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedParts.slice(0, 4).map((part, idx) => {
                              const pieceColor = getPartColor(part.piece.originalId || part.piece.id).hex;
                              return (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                  <td className="px-1.5 py-1 font-medium">
                                    <span
                                      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold"
                                      style={{
                                        backgroundColor: `${pieceColor}30`,
                                        color: pieceColor,
                                        border: `1.5px solid ${pieceColor}`
                                      }}
                                    >
                                      {numToLetter(part.num)}
                                    </span>
                                  </td>
                                  <td className="px-1.5 py-1 text-gray-600">×{part.qty}</td>
                                  <td className="px-1.5 py-1 font-mono text-gray-700 text-[10px]">{part.piece.width?.toFixed(1)}</td>
                                  <td className="px-1.5 py-1 font-mono text-gray-700 text-[10px]">{part.piece.length?.toFixed(1)}</td>
                                </tr>
                              );
                            })}
                            {groupedParts.length > 4 && (
                              <tr>
                                <td colSpan={4} className="px-1.5 py-1 text-center text-gray-400 text-[9px]">
                                  +{groupedParts.length - 4} more...
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
              {layouts.length > 8 && (
                <p className="text-center text-sm text-gray-400 py-2">
                  +{layouts.length - 8} more sheet{layouts.length - 8 > 1 ? 's' : ''}...
                </p>
              )}
            </div>
          </div>

          {/* Fixed Upgrade Overlay - Centered */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-xl shadow-2xl mx-4 pointer-events-auto max-w-xs overflow-hidden">
              {/* Orange Header */}
              <div className="bg-[#D66A28] p-4 text-white">
                <div className="flex items-center justify-center gap-2">
                  <Sparkles className="w-6 h-6" />
                  <h3 className="text-lg font-bold">Pro Feature: Shop Sheet</h3>
                </div>
              </div>
              {/* Content */}
              <div className="p-5">
                <ul className="text-sm text-gray-600 space-y-2 mb-4 text-left">
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-4 h-4 text-sage-500 mt-0.5 flex-shrink-0" />
                    <span>Interactive cut checklist</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Palette className="w-4 h-4 text-sage-500 mt-0.5 flex-shrink-0" />
                    <span>Color & print friendly formatting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-sage-500 mt-0.5 flex-shrink-0" />
                    <span>Shareable project files</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Printer className="w-4 h-4 text-sage-500 mt-0.5 flex-shrink-0" />
                    <span>Print functionality</span>
                  </li>
                </ul>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    promptUpgrade('Upgrade to Pro for interactive cut checklists, shareable project files, and print capabilities.');
                  }}
                  className="w-full bg-[#D66A28] text-white font-semibold py-3 px-4 rounded-lg hover:bg-[#c25e23] transition-colors"
                >
                  Upgrade to Pro
                </button>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="w-full mt-2 py-2 text-gray-500 text-sm font-medium"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileApp;
